import React, { useState, useCallback, useEffect } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  alpha,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
  IconButton,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import { useNotification, useTranslate } from "@refinedev/core";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
import { CountryPhoneInput } from "../../components/PhoneInput";
import axiosInstance from "../../api/axios-config";

interface SubGroup {
  id: number;
  name: string;
  parentId: number | null;
  depth?: number;
}

interface OperatorInviteModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ActionType = "existing" | "new";
type Step = "form" | "confirmation";

export const OperatorInviteModal: React.FC<OperatorInviteModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const { open: notify } = useNotification();
  const translate = useTranslate();

  // Step & mode
  const [step, setStep] = useState<Step>("form");
  const [actionType, setActionType] = useState<ActionType>("existing");

  // Form fields
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [externalId, setExternalId] = useState("");
  const [subGroupId, setSubGroupId] = useState<number | null>(null);

  // Sub-groups
  const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
  const [loadingSubGroups, setLoadingSubGroups] = useState(false);

  // Validation errors
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [externalIdError, setExternalIdError] = useState("");

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch sub-groups when modal opens
  useEffect(() => {
    if (!open) return;
    const fetchSubGroups = async () => {
      setLoadingSubGroups(true);
      try {
        const response = await axiosInstance.get("/sub-groups");
        if (Array.isArray(response.data)) {
          const calculateDepth = (groups: SubGroup[], parentId: number | null = null, depth = 0): SubGroup[] => {
            return groups
              .filter((g) => g.parentId === parentId)
              .flatMap((g) => [
                { ...g, depth },
                ...calculateDepth(groups, g.id, depth + 1),
              ]);
          };
          setSubGroups(calculateDepth(response.data));
        }
      } catch (error) {
        console.error("Failed to fetch sub-groups:", error);
      } finally {
        setLoadingSubGroups(false);
      }
    };
    fetchSubGroups();
  }, [open]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Reset all state
  const resetState = useCallback(() => {
    setStep("form");
    setActionType("existing");
    setEmail("");
    setPhone("");
    setFirstName("");
    setLastName("");
    setExternalId("");
    setSubGroupId(null);
    setEmailError("");
    setPhoneError("");
    setExternalIdError("");
    setIsSubmitting(false);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Validation
  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError("L'email est obligatoire");
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError("Format d'email invalide");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePhone = (value: string): boolean => {
    if (!value) {
      setPhoneError("Le numéro de téléphone est obligatoire");
      return false;
    }
    try {
      if (!isValidPhoneNumber(value)) {
        setPhoneError("Format de téléphone invalide");
        return false;
      }
      setPhoneError("");
      return true;
    } catch {
      setPhoneError("Format de téléphone invalide");
      return false;
    }
  };

  const validateExternalId = (value: string): boolean => {
    if (!value.trim()) {
      setExternalIdError("L'identifiant externe est obligatoire");
      return false;
    }
    setExternalIdError("");
    return true;
  };

  // Go to confirmation step
  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();

    setEmailError("");
    setPhoneError("");
    setExternalIdError("");

    let isValid = true;
    isValid = validateEmail(email) && isValid;
    isValid = validateExternalId(externalId) && isValid;

    if (actionType === "existing") {
      isValid = validatePhone(phone) && isValid;
    }

    if (!isValid) {
      notify?.({
        type: "error",
        message: "Erreur de validation",
        description: "Veuillez corriger les erreurs dans le formulaire",
      });
      return;
    }

    setStep("confirmation");
  };

  // Submit to API
  const handleConfirm = async () => {
    setIsSubmitting(true);

    try {
      if (actionType === "existing") {
        // Link existing user via provider endpoint
        await axiosInstance.post("/monitoring/providers/OPERATOR/link-user", {
          email: email.trim(),
          phone: phone.trim(),
          externalId: externalId.trim(),
          ...(subGroupId ? { subGroupId } : {}),
        });

        notify?.({
          type: "success",
          message: "Liaison effectuée avec succès",
          description: `L'utilisateur ${email} a été lié avec l'identifiant ${externalId}`,
        });
      } else {
        // Create new user + monitoring
        await axiosInstance.post("/monitoring/operators/invite-users", {
          invites: [
            {
              email: email.trim(),
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              externalId: externalId.trim(),
              ...(subGroupId ? { subGroupId } : {}),
            },
          ],
        });

        notify?.({
          type: "success",
          message: "Invitation envoyée",
          description: `Un nouveau compte a été créé pour ${email}`,
        });
      }

      resetState();
      onClose();
      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue";

      notify?.({
        type: "error",
        message: "Erreur lors de l'invitation",
        description: errorMessage,
      });

      // Go back to form on error so user can fix
      setStep("form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActionType(e.target.value as ActionType);
    setEmailError("");
    setPhoneError("");
    setExternalIdError("");
  };

  // Check if "Next" button should be disabled
  const isNextDisabled =
    !email ||
    !externalId.trim() ||
    (actionType === "existing" && !phone) ||
    (actionType === "new" && (!firstName.trim() || !lastName.trim()));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {step === "form"
              ? translate("users.operator_invite.title", "Inviter un utilisateur")
              : "Confirmer l'invitation"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      {step === "form" ? (
        /* ───────── FORM STEP ───────── */
        <>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 3 }}>
              {translate(
                "users.operator_invite.info",
                "Invitez un utilisateur à être suivi par votre service"
              )}
            </Alert>

            <form id="operator-invite-form" onSubmit={handleNext}>
              <Stack spacing={3}>
                {/* Radio: existing vs new */}
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    Type d'action
                  </FormLabel>
                  <RadioGroup
                    row
                    value={actionType}
                    onChange={handleActionTypeChange}
                    sx={{ gap: 2 }}
                  >
                    <FormControlLabel
                      value="existing"
                      control={<Radio />}
                      label="Compte Safee existant"
                      sx={{
                        flex: 1,
                        border: `2px solid ${actionType === "existing" ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1.5,
                        m: 0,
                        bgcolor:
                          actionType === "existing"
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                        transition: "all 0.2s",
                      }}
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Nouveau compte Safee"
                      sx={{
                        flex: 1,
                        border: `2px solid ${actionType === "new" ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1.5,
                        m: 0,
                        bgcolor:
                          actionType === "new"
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                        transition: "all 0.2s",
                      }}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Email */}
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => validateEmail(email)}
                  error={!!emailError}
                  helperText={emailError || "Email de l'utilisateur"}
                  placeholder="exemple@email.com"
                />

                {/* Phone (existing only) */}
                {actionType === "existing" && (
                  <CountryPhoneInput
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      if (phoneError && value) validatePhone(value);
                    }}
                    onBlur={() => validatePhone(phone)}
                    error={!!phoneError}
                    helperText={phoneError || "Le numéro sera formaté au format international"}
                    required
                    label="Numéro de téléphone"
                    placeholder="6 12 34 56 78"
                  />
                )}

                {/* FirstName + LastName (new only) */}
                {actionType === "new" && (
                  <>
                    <TextField
                      fullWidth
                      required
                      label={translate("users.operator_invite.firstName", "Prénom")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      required
                      label={translate("users.operator_invite.lastName", "Nom")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </>
                )}

                {/* External ID (always) */}
                <TextField
                  fullWidth
                  required
                  label="Identifiant externe"
                  value={externalId}
                  onChange={(e) => {
                    setExternalId(e.target.value);
                    if (externalIdError) validateExternalId(e.target.value);
                  }}
                  onBlur={() => validateExternalId(externalId)}
                  error={!!externalIdError}
                  helperText={externalIdError || "Identifiant de l'utilisateur dans votre système"}
                  placeholder="ABC-12345"
                />

                {/* Sub-group (optional) */}
                {subGroups.length > 0 && (
                  <Autocomplete
                    options={subGroups}
                    getOptionLabel={(option) => option.name}
                    value={subGroups.find((sg) => sg.id === subGroupId) || null}
                    onChange={(_, newValue) => setSubGroupId(newValue?.id || null)}
                    loading={loadingSubGroups}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sous-groupe"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingSubGroups ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        helperText="Rattacher l'utilisateur à un sous-groupe (optionnel)"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ pl: (option.depth || 0) * 2 }}>
                          {option.name}
                        </Box>
                      </li>
                    )}
                  />
                )}
              </Stack>
            </form>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleClose}>
              {translate("common.cancel", "Annuler")}
            </Button>
            <Button
              type="submit"
              form="operator-invite-form"
              variant="contained"
              disabled={isNextDisabled}
            >
              Suivant
            </Button>
          </DialogActions>
        </>
      ) : (
        /* ───────── CONFIRMATION STEP ───────── */
        <>
          <DialogContent>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Type d'action
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {actionType === "existing"
                      ? "Liaison d'un compte Safee existant"
                      : "Création d'un nouveau compte Safee"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {email}
                  </Typography>
                </Box>

                {actionType === "existing" && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Numéro de téléphone
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {(() => {
                        try {
                          const parsed = parsePhoneNumber(phone);
                          return parsed?.formatInternational() || phone;
                        } catch {
                          return phone;
                        }
                      })()}
                    </Typography>
                  </Box>
                )}

                {actionType === "new" && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Nom complet
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {firstName} {lastName}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Identifiant externe
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mt: 0.5, fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    {externalId}
                  </Typography>
                </Box>

                {subGroupId && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Sous-groupe
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {subGroups.find((sg) => sg.id === subGroupId)?.name || "-"}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Veuillez vérifier les informations ci-dessus avant de confirmer.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setStep("form")} disabled={isSubmitting}>
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined
              }
            >
              {isSubmitting ? "En cours..." : "Confirmer"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
